import { useEffect } from 'react';
import { RefType } from '@constants/entities/project';
import useProjectData from '@hooks/useProjectData';
import {
  useModelFilterActions,
  useModelFiltersStore,
} from '@store/zustand/model-filters';

/**
 * Clears the live CIS-18 reference selection once the project's reference
 * model no longer includes CIS-18.
 *
 * The category's root row disappears with the reference model, so a
 * selection left behind would keep filtering the canvas with nothing in
 * the popover to untick and only a chip to clear it from. The project
 * query refreshes `settingsData` after a settings save, so the reference
 * model is read from it and the selection cleared whenever it changes; it
 * is checked again whenever the store's scope key changes, because a mode
 * or workspace switch restores a parked selection that was never checked
 * against this setting. No reference model — the project still loading —
 * clears nothing, so a selection made before the project arrived is not
 * lost to a blank. Clearing only ever removes values, so it can only
 * reveal entities: it closes no drawer and needs no prompt. It writes
 * nothing while the selection is already empty.
 */
export const useReconcileCisFilters = () => {
  const [{ settingsData }] = useProjectData();
  const scopeKey = useModelFiltersStore((state) => state.scopeKey);
  const hasSelection = useModelFiltersStore((state) => state.cis.length > 0);
  const { clearCategory } = useModelFilterActions();
  const referenceModel = settingsData?.reference_model;
  const isOffered = Boolean(referenceModel?.includes(RefType.CIS));

  useEffect(() => {
    if (referenceModel && !isOffered && hasSelection) {
      clearCategory('cis');
    }
  }, [referenceModel, isOffered, hasSelection, scopeKey]);
};
